const ARENA_STORAGE_KEY = 'reverse_arena_v3';
const MAX_ARENA_VALUE = 2_500_000;
const DEFAULT_MAX_AGE = 24 * 60 * 60 * 1000;

function parse(value) {
  if (!value || typeof value !== 'string') return null;
  try { return JSON.parse(value); } catch { return null; }
}

export function createArenaDeviceStore({
  storage,
  now = () => Date.now(),
  maxAge = DEFAULT_MAX_AGE,
  delayMs = 500,
} = {}) {
  let timer = null;
  let pending = null;
  let lastSerialized = '';
  let writeChain = Promise.resolve(false);

  async function load(userId) {
    if (!userId || !storage?.get) return null;
    const snapshot = parse(await storage.get(ARENA_STORAGE_KEY));
    if (!snapshot || String(snapshot.userId || '') !== String(userId)) return null;
    if (!Number.isFinite(snapshot.savedAt) || now() - snapshot.savedAt > maxAge) return null;
    lastSerialized = JSON.stringify(snapshot.data || {});
    return snapshot.data && typeof snapshot.data === 'object' ? snapshot.data : null;
  }

  function write(userId, data) {
    if (!userId || !data || !storage?.set) return Promise.resolve(false);
    let dataValue;
    try { dataValue = JSON.stringify(data); } catch { return Promise.resolve(false); }
    if (dataValue === lastSerialized) return Promise.resolve(true);
    const value = JSON.stringify({ userId: String(userId), savedAt: now(), data });
    if (value.length > MAX_ARENA_VALUE) return Promise.resolve(false);
    writeChain = writeChain.catch(() => false).then(async () => {
      const saved = await storage.set(ARENA_STORAGE_KEY, value);
      if (saved) lastSerialized = dataValue;
      return saved;
    });
    return writeChain;
  }

  function schedule(userId, data) {
    pending = { userId, data };
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => {
      timer = null;
      const next = pending;
      pending = null;
      if (next) void write(next.userId, next.data);
    }, delayMs);
  }

  async function flush() {
    if (timer) clearTimeout(timer);
    timer = null;
    const next = pending;
    pending = null;
    if (next) await write(next.userId, next.data);
    await writeChain;
  }

  async function clear() {
    if (timer) clearTimeout(timer);
    timer = null;
    pending = null;
    lastSerialized = '';
    await writeChain.catch(() => false);
    return storage?.remove ? storage.remove(ARENA_STORAGE_KEY) : false;
  }

  return { load, schedule, flush, clear, key: ARENA_STORAGE_KEY };
}
